import i18n from 'i18next';
import {
  indexOf,
  isNull,
  isUndefined,
  join,
  mapValues,
  omitBy,
  some,
} from 'lodash';
import { SingleSelectItem } from '@/components/ui/SingleSelect';

export const getTranslationKey = (key: string, fallbackKey: string) => {
  return i18n.exists(key) ? key : fallbackKey;
};

export const showIf = <T extends Object, K extends keyof T, E>(
  form: T | null,
  key: K,
  values: T[K][],
  element: E,
) => {
  if (!form) {
    return null;
  }

  return some(values, (value) => form[key] === value) ? element : null;
};

export const getFormKey = (...keys: string[]) => {
  return join(keys, '.');
};

export const normalizeData = <T extends object, K extends keyof T>(
  object: T,
  omitKeys: K[],
): Partial<T> => {
  const result = omitBy(object, (value, key) => {
    if (typeof value === 'boolean') {
      return omitKeys.includes(key as K);
    }
    return isUndefined(value) || isNull(value) || omitKeys.includes(key as K);
  });

  return result;
};

export const getSelectItemsFromEnum = <T extends object>(
  enumValues: T,
): SingleSelectItem[] => {
  return Object.values(enumValues).map((value) => ({
    label: value,
    value,
  }));
};

export const mapToNumbers = <T extends object, K extends keyof T>(
  object: T,
  keys: K[],
  type: 'float' | 'int' = 'int',
): Omit<T, K> & Record<K, number> => {
  // lodash mapValues returns a loose type — cast is unavoidable here
  return mapValues(object, (value, key) => {
    if (indexOf(keys, key as K) === -1 || typeof value !== 'string') {
      return value;
    }

    let normalizedValue = value.trim();
    if (normalizedValue.includes(',')) {
      normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
    }

    return type === 'float'
      ? parseFloat(normalizedValue)
      : parseInt(normalizedValue);
  }) as unknown as Omit<T, K> & Record<K, number>;
};
